import { AccountActivityRecord, IdProviderUser, SalesRep, SalesRepStatus } from 'types';

import { api } from './api';

interface SalesRepSearchResponse {
  pageNum: number;
  total: number;
  salesReps: SalesRep[];
}

export interface SalesRepHistoryResponse {
  data: AccountActivityRecord[];
  total: number;
  pageNum: number;
  nextCursor?: string;
}

interface GetSalesRepHistoryArgs {
  salesRepId?: string;
  limit?: number;
  cursor?: string;
}

export const salesRepApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSalesReps: build.query<SalesRepSearchResponse, Record<any, any>>({
      query: (queryParams) => {
        return {
          url: `sales-reps${
            queryParams &&
            '?' +
              new URLSearchParams({
                ...queryParams,
              }).toString()
          }`,
        };
      },
      transformResponse: (response: any) => {
        const { data, pageNum, total } = response || {};
        return {
          pageNum,
          total,
          salesReps: data?.salesReps,
        };
      },
      providesTags: () => [{ type: 'SalesReps', id: 'LIST' }],
    }),
    getSalesRep: build.query<SalesRep, { id: string }>({
      query: ({ id }) => ({ url: `sales-reps/${id}` }),
      providesTags: (_result, _error, { id }) => [{ type: 'SalesReps', id }],
    }),
    updateSalesRep: build.mutation<SalesRep, { id: string; status: SalesRepStatus; reason: string }>({
      query: ({ id, status, reason }) => ({
        url: `sales-reps/${id}`,
        method: 'PATCH',
        body: { status, reason },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'SalesReps', id: 'LIST' },
        { type: 'SalesReps', id },
        { type: 'SalesReps', id: 'HISTORY' },
      ],
    }),
    getSalesRepProviderUserBySalesRepId: build.query<IdProviderUser[], { salesRepId: string }>({
      query: ({ salesRepId }) => ({
        url: `sales-reps/${salesRepId}/provider-user`,
      }),
    }),
    getSalesRepListFilters: build.query<any, void>({
      query: () => {
        return {
          url: `sales-reps/filters`,
        };
      },
    }),
    getSalesRepHistory: build.query<SalesRepHistoryResponse, GetSalesRepHistoryArgs | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.salesRepId) params.set('salesRepId', args.salesRepId);
        if (args?.limit !== undefined) params.set('limit', String(args.limit));
        if (args?.cursor) params.set('cursor', args.cursor);
        const qs = params.toString();
        return { url: `sales-reps/history${qs ? `?${qs}` : ''}` };
      },
      providesTags: () => [{ type: 'SalesReps', id: 'HISTORY' }],
    }),
    getSalesRepHistoryCursor: build.infiniteQuery<
      SalesRepHistoryResponse,
      { salesRepId?: string } | void,
      { limit: number; salesRepId?: string; cursor?: string }
    >({
      infiniteQueryOptions: {
        initialPageParam: { limit: 20, cursor: undefined },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage.nextCursor) {
            return undefined;
          }
          return {
            ...lastPageParam,
            cursor: lastPage.nextCursor,
          };
        },
      },
      query: ({ queryArg, pageParam: { limit, cursor, salesRepId } }) => {
        const params = new URLSearchParams();
        const repId = salesRepId ?? queryArg?.salesRepId;
        if (repId) params.set('salesRepId', repId);
        params.set('limit', String(limit));
        if (cursor) params.set('cursor', cursor);
        return { url: `sales-reps/history?${params.toString()}` };
      },
      providesTags: () => [{ type: 'SalesReps', id: 'HISTORY' }],
    }),
  }),
});

export const {
  useGetSalesRepsQuery,
  useLazyGetSalesRepsQuery,
  useGetSalesRepListFiltersQuery,
  useUpdateSalesRepMutation,
  useGetSalesRepQuery,
  useGetSalesRepProviderUserBySalesRepIdQuery,
  useGetSalesRepHistoryQuery,
  useGetSalesRepHistoryCursorInfiniteQuery,
} = salesRepApi;
